import { useEffect, useMemo, useState } from "react";
import { useLocale, DEFAULT_LOCALE, type Locale } from "@/i18n/locale";

export type StringDict = Record<string, string>;

/**
 * Every `src/i18n/ui/strings/<page>.<locale>.ts` module exports a default
 * map of English source string -> translation. They are collected with globs
 * so new page dictionaries need no registration step.
 *
 * German (the launched second language) is bundled eagerly. The locales that
 * are not launched yet load on demand, so their dictionaries never weigh down
 * an English or German page load. `/$lang` awaits the load before rendering and
 * before hydrating, so translated markup is never replaced by English.
 */
const eagerModules = import.meta.glob<{ default: StringDict }>("./strings/*.de.ts", {
  eager: true,
});
const lazyModules = import.meta.glob<{ default: StringDict }>([
  "./strings/*.ts",
  "!./strings/*.de.ts",
]);

const localeOf = (path: string) => {
  const match = /\.([a-z]{2})\.ts$/.exec(path);
  return match ? (match[1] as Locale) : null;
};

const byLocale = new Map<Locale, StringDict>();

/**
 * JSX source strings often carry line breaks and indentation, so the same copy
 * can reach `t()` with different whitespace than the dictionary key. A second,
 * whitespace-collapsed index makes the lookup tolerant of that.
 */
const normalize = (text: string) => text.replace(/\s+/g, " ").trim();
const normalizedByLocale = new Map<Locale, StringDict>();

function register(locale: Locale, dict: StringDict) {
  const target = byLocale.get(locale) ?? {};
  Object.assign(target, dict);
  byLocale.set(locale, target);
  const flat = normalizedByLocale.get(locale) ?? {};
  for (const [key, value] of Object.entries(dict)) flat[normalize(key)] = value;
  normalizedByLocale.set(locale, flat);
}

for (const [path, mod] of Object.entries(eagerModules)) {
  const locale = localeOf(path);
  if (locale) register(locale, mod.default ?? {});
}

const loadedLocales = new Set<Locale>(byLocale.keys());
const bundledLocales = new Set<Locale>(byLocale.keys());

/** True for locales whose dictionaries ship with the app bundle (no lazy load). */
export function isBundledLocale(locale: string): boolean {
  return locale === DEFAULT_LOCALE || bundledLocales.has(locale as Locale);
}
const pendingLoads = new Map<Locale, Promise<void>>();
const listeners = new Set<() => void>();
let version = 0;

/**
 * Loads the on-demand dictionaries for a locale. Returns a promise while the
 * dictionary is still missing and `undefined` once the strings are in memory,
 * so callers can suspend only when they have to.
 */
export function loadLocaleStrings(locale: string): Promise<void> | undefined {
  if (locale === DEFAULT_LOCALE) return undefined;
  const target = locale as Locale;
  if (loadedLocales.has(target)) return undefined;
  const existing = pendingLoads.get(target);
  if (existing) return existing;
  const paths = Object.keys(lazyModules).filter((path) => localeOf(path) === target);
  if (paths.length === 0) {
    loadedLocales.add(target);
    return undefined;
  }
  const promise = Promise.all(paths.map((path) => lazyModules[path]!()))
    .then((mods) => {
      for (const mod of mods) register(target, mod.default ?? {});
      loadedLocales.add(target);
      pendingLoads.delete(target);
      version += 1;
      for (const listener of listeners) listener();
    });
  pendingLoads.set(target, promise);
  return promise;
}

/** Re-renders consumers once an on-demand dictionary finishes loading. */
function useStringsVersion() {
  const [, setLocalVersion] = useState(version);
  useEffect(() => {
    const listener = () => setLocalVersion(version);
    listeners.add(listener);
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return version;
}

function lookup(text: string, dict: StringDict | undefined, flat: StringDict | undefined) {
  if (!dict) return text;
  return dict[text] ?? flat?.[normalize(text)] ?? text;
}

/** Translate a single English source string for `locale`. */
export function translate(text: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return text;
  return lookup(text, byLocale.get(locale), normalizedByLocale.get(locale));
}

/**
 * Returns `t()` for the active locale. Pass the approved English copy as the
 * key: missing translations fall back to English, never to a blank string.
 */
export function useT() {
  const locale = useLocale();
  const stringsVersion = useStringsVersion();
  return useMemo(() => {
    const dict = locale === DEFAULT_LOCALE ? undefined : byLocale.get(locale);
    const flat = locale === DEFAULT_LOCALE ? undefined : normalizedByLocale.get(locale);
    /** Tolerant lookup: non-string children (arrays, elements) pass through. */
    function t(value: string): string;
    function t<T>(value: T): T;
    function t(value: unknown) {
      if (typeof value !== "string") return value;
      return dict ? lookup(value, dict, flat) : value;
    }
    return t;
  }, [locale, stringsVersion]);
}
